import { Component } from 'react'
import { connect } from 'react-redux'
import { Navigate, Route, Routes } from 'react-router'
import { Modal } from 'reactstrap'
import PropTypes from 'prop-types'

import { PacoLogsModal } from '../../@paco/compositions'
import checkPermission from '../../@paco/helpers/permissions/checkPermission'
import { getEventLogsByEntityUuid } from '../../@paco/redux/eventLogs/eventLogsActions'
import { getPacoPayrollPeriods } from '../../@paco/redux/payrollPeriods/payrollPeriodsActions'
import { setToast } from '../../@paco/redux/toasts/toastsReducer'
import { addTrack, deleteTrack, editTrack } from '../../@paco/redux/tracks/tracksActions'
import { setError as setTrackError } from '../../@paco/redux/tracks/tracksReducer'
import { ToastType } from '../../@paco/types'

import ButtonAuthenticate from '../../components/ButtonAuthenticate/ButtonAuthenticate'
import DeleteResourceForm from '../../components/forms/DeleteResourceForm/DeleteResourceForm'
import { TRACK_TYPE_OPEN } from '../../constants'
import { translate } from '../../helpers/translations/translator'
import { withRouter } from '../../helpers/WithRouter'
import { clearResourceLogs } from '../../redux/logs/logsActions'
import { getTracks, getTracksByUserAndDate } from '../../redux/tracks/tracksActions'
import { getEmployees } from '../../redux/users/usersActions'
import FinishedTracks from './FinishedTracks'
import AddTrack from './forms/AddTrack'
import EditTrackLegacy from './forms/EditTrackLegacy'
import { getEditTrackFormTitle } from './helpers'
import OpenTracks from './OpenTracks'
import ReviewTracks from './ReviewTracks'
import { TrackRoutes } from './TrackRoutes'

import './Tracks.scss'

class LegacyTracksRouteGroup extends Component {
    state = {
        addTrackForm: false,
        editTrackForm: false,
        editTrackData: null,
        editTrackType: null,
        trackToDelete: null,
        logsModal: false,
    }

    componentDidMount() {
        const { dispatch } = this.props
        dispatch(getEmployees(
            undefined,
            undefined,
            undefined,
            true,
        ))
        dispatch(clearResourceLogs())
        dispatch(getPacoPayrollPeriods())
    }

    componentDidUpdate() {
        const { dispatch } = this.props

        if (this.props.tracksError) {
            dispatch(setToast({
                text: this.props.tracksError,
                type: ToastType.fail,
                shouldFadeOutAutomatically: false,
            }))

            dispatch(setTrackError(''))
        }
    }

    addTrack = () => {
        this.toggleAddTrack(true)
    }

    deleteTrack = (track) => {
        this.toggleDeleteTrack(false, track)
    }

    reviewTrack = async (track, departmentId, data, comment, approved) => {
        const { dispatch } = this.props
        const { editTrackType } = this.state
        this.setState({ editTrackForm: false })
        await dispatch(editTrack(
            track.id,
            departmentId,
            data,
            comment,
            approved,
            track.endedInManagerApp,
        ))

        if (editTrackType === TRACK_TYPE_OPEN) {
            dispatch(getTracks(TRACK_TYPE_OPEN))
        }
    }

    toggleAddTrack = (active) => {
        const { addTrackForm } = this.state
        this.setState({
            addTrackForm: active || ! addTrackForm,
        })
    }

    toggleDeleteTrack = (active, track) => {
        const { deleteTrackForm } = this.state
        this.setState({
            trackToDelete: track,
            deleteTrackForm: active || ! deleteTrackForm,
        })
    }

    dispatchOpenLogsModal = (id) => {
        if (id) {
            this.props.dispatch(getEventLogsByEntityUuid(id))
        }

        this.setState({ logsModal: true })
    }

    dispatchCloseLogsModal = () => {
        this.setState({ logsModal: false })
    }

    activeReviewForm = (track, type) => {
        this.setState({
            editTrackForm: true,
            editTrackData: track,
            editTrackType: type,
        })
    }

    dispatchAddTrack = async (userId, departmentId, data, comment) => {
        const { dispatch } = this.props
        this.toggleAddTrack(false)
        await dispatch(addTrack(userId, departmentId, data, comment))
    }

    dispatchDeleteTrack = async () => {
        const { dispatch } = this.props
        const { trackToDelete } = this.state
        this.toggleDeleteTrack(false)
        await dispatch(deleteTrack(trackToDelete.id))
    }

    dispatchGetTracksForUserOnDate = (userId, date) => {
        const { dispatch } = this.props
        dispatch(getTracksByUserAndDate(userId, date))
    }

    render() {
        const {
            addTrackForm,
            editTrackForm,
            editTrackData,
            editTrackType,
            deleteTrackForm,
        } = this.state
        const {
            departments,
            currentUser,
            permissions,
            settings,
            userTracksOnDate,
            payrollPeriods,
            users = [],
            eventLogs = null,
            eventLogsLoading,
        } = this.props

        const propsWithDefaults = {
            users: [],
            logs: null,
            ...this.props,
        }

        const canViewLogs = checkPermission(permissions, 'view-all-logs')
        const canAddTracks = checkPermission(permissions, 'add-new-tracks')
            || checkPermission(permissions, 'add-new-manager-tracks')
            || checkPermission(permissions, 'add-new-junior-manager-tracks')
            || checkPermission(permissions, 'add-new-employee-tracks')
        const trackTranslation = translate('common.track')
        const editTrackTitle = getEditTrackFormTitle(editTrackType)

        return (
            <>
                <Routes>
                    <Route
                        path={TrackRoutes.open()}
                        element={(
                            <OpenTracks
                                {...propsWithDefaults}
                                onAddTrackClick={this.addTrack}
                                onReviewTrackClick={this.activeReviewForm}
                                canAddTracks={canAddTracks}
                            />
                        )}
                    />
                    <Route
                        path={TrackRoutes.review()}
                        element={(
                            <ReviewTracks
                                {...propsWithDefaults}
                                onAddTrackClick={this.addTrack}
                                onReviewTrackClick={this.activeReviewForm}
                                canAddTracks={canAddTracks}
                            />
                        )}
                    />
                    <Route
                        path={TrackRoutes.finished()}
                        element={(
                            <FinishedTracks
                                {...propsWithDefaults}
                                onAddTrackClick={this.addTrack}
                                onReviewTrackClick={this.activeReviewForm}
                                onDeleteTrackClick={this.deleteTrack}
                                onShowLogsClick={canViewLogs ? this.dispatchOpenLogsModal : null}
                                canAddTracks={canAddTracks}
                            />
                        )}
                    />
                    <Route path="/" element={<Navigate to={TrackRoutes.review()} />} />
                </Routes>
                <Modal size="lg" isOpen={addTrackForm}>
                    <AddTrack
                        departments={departments}
                        users={users}
                        permissions={permissions}
                        onSubmit={this.dispatchAddTrack}
                        onCancel={() => this.toggleAddTrack(false)}
                        onEmployeeSelect={this.dispatchGetTracksForUserOnDate}
                        userTracksOnDate={userTracksOnDate}
                        payrollPeriods={payrollPeriods}
                        currentUserId={currentUser.id}
                    />
                </Modal>
                <Modal size="lg" isOpen={editTrackForm} className="form-edit-track">
                    <EditTrackLegacy
                        canEdit={editTrackType !== TRACK_TYPE_OPEN}
                        permissions={permissions}
                        settings={settings}
                        title={editTrackTitle}
                        onSubmit={this.reviewTrack}
                        onCancel={() => this.setState({ editTrackForm: false })}
                        payrollPeriods={payrollPeriods}
                        onShowLogsClick={canViewLogs ? () => this.dispatchOpenLogsModal(editTrackData.id) : null}
                        track={editTrackData}
                    />
                </Modal>
                <Modal size="md" isOpen={deleteTrackForm}>
                    <DeleteResourceForm
                        title={translate('common.deleteResource', { resource: trackTranslation })}
                        intro={translate('common.deleteResourceIntro', { resource: trackTranslation.toLowerCase() })}
                        onSubmit={this.dispatchDeleteTrack}
                        onCancel={() => this.toggleDeleteTrack(false)}
                    />
                </Modal>
                <PacoLogsModal
                    isOpen={this.state.logsModal && ! eventLogsLoading}
                    logs={eventLogs}
                    title={translate('logs.trackLog')}
                    emptyMessage={translate('logs.emptyTrackLog')}
                    onClose={this.dispatchCloseLogsModal}
                />
                {canAddTracks && (
                    <ButtonAuthenticate
                        onClick={this.addTrack}
                        mobile
                    />
                )}
            </>
        )
    }
}

LegacyTracksRouteGroup.propTypes = {
    dispatch: PropTypes.func.isRequired,
    loading: PropTypes.bool.isRequired,
    tracksLoading: PropTypes.bool.isRequired,
    tracks: PropTypes.array.isRequired,
    tracksError: PropTypes.string,
    filter: PropTypes.object.isRequired,
    timeMode: PropTypes.string.isRequired,
    startDate: PropTypes.object.isRequired,
    endDate: PropTypes.object.isRequired,
    selectedDate: PropTypes.object.isRequired,
    pagination: PropTypes.object.isRequired,
    currentUser: PropTypes.object.isRequired,
    currentUserRole: PropTypes.object.isRequired,
    exportCSVRequest: PropTypes.bool.isRequired,
    users: PropTypes.array,
    departments: PropTypes.array.isRequired,
    departmentOptions: PropTypes.array.isRequired,
    employmentTypeOptions: PropTypes.array.isRequired,
    permissions: PropTypes.array.isRequired,
    settings: PropTypes.array.isRequired,
    payrollPeriods: PropTypes.array.isRequired,
    employmentTypes: PropTypes.array.isRequired,
    payrollPeriodsLoading: PropTypes.bool.isRequired,
    userTracksOnDate: PropTypes.array.isRequired,
    absences: PropTypes.array.isRequired,
    absencesLoading: PropTypes.bool.isRequired,
    eventLogs: PropTypes.array,
    eventLogsLoading: PropTypes.bool.isRequired,
    loadingTrackIds: PropTypes.array.isRequired,
}

function mapStateToProps(state) {
    return {
        tracks: state.tracksReducer.tracks,
        tracksToReview: state.pacoTracksReducer.tracksToReview,
        finishedTracks: state.pacoTracksReducer.finishedTracks,
        finishedTracksSorting: state.pacoTracksReducer.finishedTracksSorting,
        loading: state.tracksReducer.loading,
        tracksLoading: state.pacoTracksReducer.isLoading,
        tracksError: state.pacoTracksReducer.error,
        trackType: state.tracksReducer.trackType,
        exportCSVRequest: state.tracksReducer.exportCSVRequest,
        payrollPeriods: state.pacoPayrollPeriodsReducer.payrollPeriods,
        payrollPeriodsLoading: state.appReducer.payrollPeriodsLoading,
        currentUser: state.authenticatedUserReducer.user,
        currentUserRole: state.authenticatedUserReducer.role,
        users: state.usersReducer.employees,
        filter: state.filterReducer.filter,
        timeMode: state.weekNavigatorReducer.mode,
        startDate: state.weekNavigatorReducer.startDate,
        endDate: state.weekNavigatorReducer.endDate,
        selectedDate: state.weekNavigatorReducer.selectedDate,
        pagination: state.paginationReducer,
        departments: state.authenticatedUserReducer.departments,
        departmentOptions: state.globalFiltersReducer.departmentOptions,
        employmentTypeOptions: state.globalFiltersReducer.employmentTypeOptions,
        permissions: state.authenticatedUserReducer.permissions,
        settings: state.pacoSettingsReducer.settings,
        employmentTypes: state.appReducer.employmentTypes,
        userTracksOnDate: state.tracksReducer.userTracksOnDate,
        absences: state.absencesReducer.absencesInDateRange,
        absencesLoading: state.absencesReducer.loading,
        eventLogs: state.eventLogsReducer.eventLogsByEntity,
        eventLogsLoading: state.eventLogsReducer.isLoading,
        loadingTrackIds: state.pacoTracksReducer.loadingTrackIds,
    }
}

export default withRouter(connect(mapStateToProps)(LegacyTracksRouteGroup))
